import { Check, CircleAlert } from 'lucide-react';

import { cn } from '@/_shared/lib/utils';

import type { MeasurementPlanStatus } from '../types';

interface PlanStatusStepperProps {
  status: MeasurementPlanStatus;
}

const STEPS: { id: MeasurementPlanStatus; label: string }[] = [
  { id: 'draft', label: 'Draft' },
  { id: 'approved', label: 'Approved' },
  { id: 'implementing', label: 'Implementing' },
  { id: 'implemented', label: 'Implemented' },
  { id: 'verified', label: 'Verified' },
];

function currentIndex(status: MeasurementPlanStatus): number {
  if (status === 'partial_failure') return 2;
  const idx = STEPS.findIndex((s) => s.id === status);
  return idx === -1 ? 0 : idx;
}

export function PlanStatusStepper({ status }: PlanStatusStepperProps): React.ReactElement {
  const active = currentIndex(status);
  const failed = status === 'partial_failure';

  return (
    <ol className="flex items-center gap-2" aria-label="Plan status">
      {STEPS.map((step, idx) => {
        const isActive = idx === active;
        const isComplete = idx < active;
        const isFailedStep = isActive && failed;

        return (
          <li key={step.id} className="flex items-center gap-2">
            <span
              className={cn(
                'flex size-6 items-center justify-center rounded-full border text-[0.625rem] font-semibold',
                isComplete && 'border-emerald-500 bg-emerald-500 text-white',
                isActive && !failed && 'border-primary bg-primary text-primary-foreground',
                isFailedStep && 'border-rose-500 bg-rose-500 text-white',
                !isActive && !isComplete && 'border-border bg-muted text-muted-foreground',
              )}
              aria-current={isActive ? 'step' : undefined}
            >
              {isComplete ? <Check className="size-3" /> : isFailedStep ? <CircleAlert className="size-3" /> : idx + 1}
            </span>
            <span
              className={cn(
                'text-xs',
                (isActive || isComplete) && 'font-medium text-foreground',
                !isActive && !isComplete && 'text-muted-foreground',
              )}
            >
              {step.label}
            </span>
            {idx < STEPS.length - 1 && <span className="h-px w-6 bg-border" aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
}
